import React from 'react';
import styles from '../styles/interactive-docs.module.css';
import {
    LayersIcon,
    ViewIcon,
    RulerIcon,
    MapIcon,
    SettingsIcon,
    GlobeIcon,
    PrintIcon,
    EditIcon,
    InfoIcon,
    PinIcon,
    ListIcon,
    HomeIcon,
    ForestIcon,
    AlertTriangleIcon,
    CheckCircleIcon,
    RoadIcon,
    DrawLineIcon,
    WaterIcon
} from '../../../components/Icon';
import { AdminScreenWireframe } from './AdminScreenWireframe';
import { PassportWireframe } from './PassportWireframe';

// Набор встроенных векторных иконок для карточек
const getCardIcon = (name) => {
    switch (name) {
        case 'layers':
        case 'layout':
        case 'square':
            return <LayersIcon size={18} />;
        case 'palette':
        case 'view':
            return <ViewIcon size={18} />;
        case 'cursor':
        case 'click':
        case 'map-pin':
        case 'pin':
            return <PinIcon size={18} />;
        case 'ruler':
        case 'activity':
            return <RulerIcon size={18} />;
        case 'play':
        case 'play-circle':
            return (
                <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <polygon points="10 8 16 12 10 16 10 8" fill="currentColor" />
                </svg>
            );
        case 'road':
            return <RoadIcon size={18} />;
        case 'line':
            return <DrawLineIcon size={18} />;
        case 'water':
        case 'droplet':
            return <WaterIcon size={18} />;
        case 'display':
        case 'map':
        case 'box':
            return <MapIcon size={18} />;
        case 'settings':
            return <SettingsIcon size={18} />;
        case 'globe':
        case 'satellite':
            return <GlobeIcon size={18} />;
        case 'print':
            return <PrintIcon size={18} />;
        case 'edit':
        case 'edit-2':
        case 'edit-3':
            return <EditIcon size={18} />;
        case 'file-text':
        case 'text':
        case 'document':
        case 'list':
        case 'sidebar':
            return <ListIcon size={18} />;
        case 'lock':
            return (
                <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
            );
        case 'tag':
            return (
                <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                    <line x1="7" y1="7" x2="7.01" y2="7" />
                </svg>
            );
        case 'image':
            return (
                <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                </svg>
            );
        case 'sliders':
            return (
                <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="4" y1="21" x2="4" y2="14" />
                    <line x1="4" y1="10" x2="4" y2="3" />
                    <line x1="12" y1="21" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12" y2="3" />
                    <line x1="20" y1="21" x2="20" y2="16" />
                    <line x1="20" y1="12" x2="20" y2="3" />
                    <line x1="1" y1="14" x2="7" y2="14" />
                    <line x1="9" y1="8" x2="15" y2="8" />
                    <line x1="17" y1="16" x2="23" y2="16" />
                </svg>
            );
        case 'home':
        case 'house':
            return <HomeIcon size={18} />;
        case 'trees':
        case 'tree':
        case 'forest':
        case 'park':
            return <ForestIcon size={18} />;
        case 'check':
        case 'check-circle':
            return <CheckCircleIcon size={18} />;
        case 'shield-alert':
        case 'alert':
            return <AlertTriangleIcon size={18} />;
        case 'shield':
            return (
                <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
            );
        default:
            return <InfoIcon size={18} />;
    }
};

/**
 * Парсер блоков документации:
 * - Заголовки (#, ##, ###)
 * - Разделители (---)
 * - Таблицы (| col | col |)
 * - Блоки кода (```lang)
 * - Списки (*, -, 1.)
 * - Callout цитаты (> [!NOTE] и > текст)
 * - Контейнеры Admonitions (:::tip, :::info, :::keyboard, :::warning)
 * - Сетки и карточки (:::grid cols=2 ... :::card icon="..." title="..." ... :::)
 * - Теги песочницы {{sandbox:...}}
 */
export const parseMarkdownToBlocks = (markdownText = '') => {
    if (!markdownText || typeof markdownText !== 'string') return [];
    
    const lines = markdownText.split(/\r?\n/);
    const blocks = [];
    let i = 0;

    while (i < lines.length) {
        const line = lines[i];

        // Пустая строка
        if (line.trim() === '') {
            i++;
            continue;
        }

        // Горизонтальный разделитель ---
        if (line.trim() === '---' || line.trim() === '***') {
            blocks.push({ type: 'hr' });
            i++;
            continue;
        }

        // Блок кода ```
        if (line.trim().startsWith('```')) {
            const lang = line.trim().slice(3).trim();
            const codeLines = [];
            i++;
            while (i < lines.length && !lines[i].trim().startsWith('```')) {
                codeLines.push(lines[i]);
                i++;
            }
            i++; // skip closing ```
            blocks.push({
                type: 'code',
                lang,
                code: codeLines.join('\n')
            });
            continue;
        }

        // Grid контейнер :::grid cols=2 ... :::
        if (line.trim().startsWith(':::grid')) {
            const colsMatch = line.match(/cols=(\d+)/);
            const cols = colsMatch ? parseInt(colsMatch[1], 10) : 2;
            const cardBlocks = [];
            i++;

            while (i < lines.length && lines[i].trim() !== ':::') {
                if (lines[i].trim().startsWith(':::card')) {
                    const cardHeader = lines[i].trim();
                    const iconMatch = cardHeader.match(/icon="([^"]+)"/);
                    const titleMatch = cardHeader.match(/title="([^"]+)"/);
                    const icon = iconMatch ? iconMatch[1] : 'info';
                    const title = titleMatch ? titleMatch[1] : '';

                    const cardLines = [];
                    i++;
                    while (i < lines.length && lines[i].trim() !== ':::' && !lines[i].trim().startsWith(':::card')) {
                        cardLines.push(lines[i]);
                        i++;
                    }
                    if (lines[i] && lines[i].trim() === ':::') {
                        i++; // закрыть card
                    }
                    cardBlocks.push({
                        icon,
                        title,
                        content: cardLines.join('\n').trim()
                    });
                    continue;
                }
                i++;
            }
            if (lines[i] && lines[i].trim() === ':::') {
                i++; // закрыть grid
            }
            blocks.push({
                type: 'grid',
                cols,
                cards: cardBlocks
            });
            continue;
        }

        // Одиночная карточка :::card icon="..." title="..."
        if (line.trim().startsWith(':::card')) {
            const cardHeader = line.trim();
            const iconMatch = cardHeader.match(/icon="([^"]+)"/);
            const titleMatch = cardHeader.match(/title="([^"]+)"/);
            const icon = iconMatch ? iconMatch[1] : 'info';
            const title = titleMatch ? titleMatch[1] : '';

            const cardLines = [];
            i++;
            while (i < lines.length && lines[i].trim() !== ':::') {
                cardLines.push(lines[i]);
                i++;
            }
            if (lines[i] && lines[i].trim() === ':::') {
                i++; // закрыть card
            }
            blocks.push({
                type: 'grid',
                cols: 1,
                cards: [{
                    icon,
                    title,
                    content: cardLines.join('\n').trim()
                }]
            });
            continue;
        }

        // Интерактивный/схематичный вайрфрейм интерфейса
        if (line.trim().startsWith('{{wireframe:passport}}') || line.trim() === ':::wireframe-passport' || line.trim() === ':::wireframe-object-card') {
            blocks.push({
                type: 'wireframe',
                wireframeType: 'passport'
            });
            i++;
            continue;
        }

        if (line.trim().startsWith('{{wireframe:admin}}') || line.trim() === ':::wireframe-admin') {
            blocks.push({
                type: 'wireframe',
                wireframeType: 'admin'
            });
            i++;
            continue;
        }

        // Admonition блок :::tip, :::info, :::keyboard, :::warning, :::note, :::shield
        if (/^:::(tip|info|keyboard|warning|note|important|shield)\b/.test(line.trim())) {
            const match = line.trim().match(/^:::(tip|info|keyboard|warning|note|important|shield)(?:\s+(.*))?$/);
            const calloutType = match[1];
            const title = match[2] || '';
            const contentLines = [];
            i++;
            while (i < lines.length && lines[i].trim() !== ':::') {
                contentLines.push(lines[i]);
                i++;
            }
            if (lines[i] && lines[i].trim() === ':::') {
                i++; // skip closing :::
            }
            blocks.push({
                type: 'admonition',
                calloutType,
                title,
                content: contentLines.join('\n').trim()
            });
            continue;
        }

        // Цитатный блок > (включая Lead или [!NOTE])
        if (line.trim().startsWith('>')) {
            const quoteLines = [];
            let calloutType = null;
            const firstTrim = line.trim().replace(/^>\s*/, '');
            
            if (firstTrim.startsWith('[!NOTE]')) {
                calloutType = 'note';
            } else if (firstTrim.startsWith('[!TIP]')) {
                calloutType = 'tip';
            } else if (firstTrim.startsWith('[!WARNING]')) {
                calloutType = 'warning';
            } else if (firstTrim.startsWith('[!IMPORTANT]')) {
                calloutType = 'important';
            } else {
                quoteLines.push(firstTrim);
            }

            i++;
            while (i < lines.length && lines[i].trim().startsWith('>')) {
                quoteLines.push(lines[i].trim().replace(/^>\s*/, ''));
                i++;
            }

            if (calloutType) {
                blocks.push({
                    type: 'callout',
                    calloutType,
                    text: quoteLines.join('\n')
                });
            } else {
                // Lead цитата
                blocks.push({
                    type: 'lead',
                    text: quoteLines.join(' ')
                });
            }
            continue;
        }

        // Таблица (| col | col |)
        if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
            const tableLines = [];
            while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
                tableLines.push(lines[i].trim());
                i++;
            }

            if (tableLines.length >= 2) {
                const headers = tableLines[0]
                    .split('|')
                    .slice(1, -1)
                    .map(s => s.trim());
                
                const rowLines = tableLines.slice(2);
                const rows = rowLines.map(r => 
                    r.split('|').slice(1, -1).map(s => s.trim())
                );

                blocks.push({
                    type: 'table',
                    headers,
                    rows
                });
                continue;
            }
        }

        // Заголовки
        if (line.startsWith('# ')) {
            blocks.push({ type: 'h1', text: line.replace(/^#\s+/, '') });
            i++;
            continue;
        }
        if (line.startsWith('## ')) {
            blocks.push({ type: 'h2', text: line.replace(/^##\s+/, '') });
            i++;
            continue;
        }
        if (line.startsWith('### ')) {
            blocks.push({ type: 'h3', text: line.replace(/^###\s+/, '') });
            i++;
            continue;
        }
        if (line.startsWith('#### ')) {
            blocks.push({ type: 'h4', text: line.replace(/^####\s+/, '') });
            i++;
            continue;
        }

        // Нумерованный список
        if (/^\d+\.\s+/.test(line.trim())) {
            const listItems = [];
            while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
                listItems.push(lines[i].trim().replace(/^\d+\.\s+/, ''));
                i++;
            }
            blocks.push({ type: 'ol', items: listItems });
            continue;
        }

        // Маркированный список
        if (/^[-*]\s+/.test(line.trim())) {
            const listItems = [];
            while (i < lines.length && /^[-*]\s+/.test(lines[i].trim())) {
                listItems.push(lines[i].trim().replace(/^[-*]\s+/, ''));
                i++;
            }
            blocks.push({ type: 'ul', items: listItems });
            continue;
        }

        // Виджет песочницы {{sandbox:...}}
        if (line.trim().startsWith('{{sandbox:') && line.trim().endsWith('}}')) {
            const sandboxType = line.trim().replace('{{sandbox:', '').replace('}}', '').trim();
            blocks.push({ type: 'sandbox', sandboxType });
            i++;
            continue;
        }

        // Обычный параграф
        const paragraphLines = [line];
        i++;
        while (
            i < lines.length &&
            lines[i].trim() !== '' &&
            !lines[i].startsWith('#') &&
            !lines[i].trim().startsWith('```') &&
            !lines[i].trim().startsWith('>') &&
            !lines[i].trim().startsWith('|') &&
            !lines[i].trim().startsWith(':::') &&
            !lines[i].trim().startsWith('---') &&
            !/^[-*]\s+/.test(lines[i].trim()) &&
            !/^\d+\.\s+/.test(lines[i].trim())
        ) {
            paragraphLines.push(lines[i]);
            i++;
        }
        blocks.push({ type: 'p', text: paragraphLines.join(' ') });
    }

    return blocks;
};

// Форматирование инлайн текста: **жирный**, *курсив*, `код/бейдж`, [ссылка](url)
export const renderInline = (text = '') => {
    if (!text) return null;

    const parts = [];
    let remaining = text;
    let keyIdx = 0;

    while (remaining.length > 0) {
        // Картинка ![alt](url)
        const imgMatch = remaining.match(/^(.*?)!\[([^\]]*)\]\(([^)]+)\)(.*)$/);
        // Тег переноса <br> или <br/>
        const brMatch = remaining.match(/^(.*?)<br\s*\/?>\s*(.*)$/i);
        // Код `...`
        const codeMatch = remaining.match(/^(.*?)`([^`]+)`(.*)$/);
        // Жирный **...**
        const boldMatch = remaining.match(/^(.*?)\*\*([^*]+)\*\*(.*)$/);
        // Курсив *...*
        const italicMatch = remaining.match(/^(.*?)\*([^*]+)\*(.*)$/);
        // Ссылка [text](url)
        const linkMatch = remaining.match(/^(.*?)\[([^\]]+)\]\(([^)]+)\)(.*)$/);

        const matches = [
            imgMatch ? { type: 'img', index: imgMatch[1].length, match: imgMatch } : null,
            brMatch ? { type: 'br', index: brMatch[1].length, match: brMatch } : null,
            codeMatch ? { type: 'code', index: codeMatch[1].length, match: codeMatch } : null,
            boldMatch ? { type: 'bold', index: boldMatch[1].length, match: boldMatch } : null,
            italicMatch ? { type: 'italic', index: italicMatch[1].length, match: italicMatch } : null,
            linkMatch ? { type: 'link', index: linkMatch[1].length, match: linkMatch } : null
        ].filter(Boolean).sort((a, b) => a.index - b.index);

        if (matches.length === 0) {
            parts.push(<span key={keyIdx++}>{remaining}</span>);
            break;
        }

        const first = matches[0];
        const m = first.match;
        const prefix = m[1];

        if (prefix) {
            parts.push(<span key={keyIdx++}>{prefix}</span>);
        }

        if (first.type === 'img') {
            parts.push(
                <span key={keyIdx++} style={{ display: 'block', textAlign: 'center', margin: '12px 0' }}>
                    <img
                        src={m[3]}
                        alt={m[2] || ''}
                        style={{
                            maxWidth: '100%',
                            maxHeight: '260px',
                            borderRadius: '6px',
                            border: '1px solid var(--color-border, #e2e8f0)',
                            objectFit: 'contain'
                        }}
                    />
                </span>
            );
            remaining = m[4];
        } else if (first.type === 'br') {
            parts.push(<br key={keyIdx++} />);
            remaining = m[2];
        } else if (first.type === 'code') {
            const val = m[2];
            // Специфические бейджи для таблицы модулей
            const isBadge = ['Интеграция', 'Экспорт', 'Инструменты', 'Визуализация'].includes(val);
            if (isBadge) {
                parts.push(
                    <span key={keyIdx++} className={`${styles.tableBadge} ${styles['badge_' + val]}`}>
                        {val}
                    </span>
                );
            } else {
                parts.push(
                    <code key={keyIdx++} className={styles.inlineCode}>
                        {val}
                    </code>
                );
            }
            remaining = m[3];
        } else if (first.type === 'bold') {
            parts.push(
                <strong key={keyIdx++} className={styles.strongText}>
                    {m[2]}
                </strong>
            );
            remaining = m[3];
        } else if (first.type === 'italic') {
            parts.push(
                <em key={keyIdx++} className={styles.italicText}>
                    {m[2]}
                </em>
            );
            remaining = m[3];
        } else if (first.type === 'link') {
            parts.push(
                <a key={keyIdx++} href={m[3]} className={styles.docLink}>
                    {m[2]}
                </a>
            );
            remaining = m[4];
        }
    }

    return parts;
};

// Отображение многострочного контента с поддержкой списков (*, -, 1.) и параграфов
export const renderRichContent = (content = '') => {
    if (!content) return null;
    const lines = content.split(/\r?\n/);
    const elements = [];
    let currentList = null;

    lines.forEach((line, idx) => {
        const trimmed = line.trim();
        if (!trimmed) {
            if (currentList) {
                elements.push(
                    <ul key={`ul-${idx}`} className={styles.docUl} style={{ margin: '8px 0' }}>
                        {currentList.map((li, lIdx) => (
                            <li key={lIdx} className={styles.docLi}>{renderInline(li)}</li>
                        ))}
                    </ul>
                );
                currentList = null;
            }
            return;
        }

        if (trimmed.startsWith('* ') || trimmed.startsWith('- ')) {
            const itemText = trimmed.slice(2);
            if (!currentList) currentList = [];
            currentList.push(itemText);
        } else if (/^\d+\.\s/.test(trimmed)) {
            const itemText = trimmed.replace(/^\d+\.\s+/, '');
            if (!currentList) currentList = [];
            currentList.push(itemText);
        } else {
            if (currentList) {
                elements.push(
                    <ul key={`ul-${idx}`} className={styles.docUl} style={{ margin: '8px 0' }}>
                        {currentList.map((li, lIdx) => (
                            <li key={lIdx} className={styles.docLi}>{renderInline(li)}</li>
                        ))}
                    </ul>
                );
                currentList = null;
            }
            elements.push(<p key={`p-${idx}`} className={styles.docP} style={{ margin: '4px 0' }}>{renderInline(trimmed)}</p>);
        }
    });

    if (currentList) {
        elements.push(
            <ul key={`ul-last`} className={styles.docUl} style={{ margin: '8px 0' }}>
                {currentList.map((li, lIdx) => (
                    <li key={lIdx} className={styles.docLi}>{renderInline(li)}</li>
                ))}
            </ul>
        );
    }

    return elements.length > 0 ? elements : renderInline(content);
};

const MarkdownRenderer = ({ markdown = '', renderSandbox }) => {
    const blocks = parseMarkdownToBlocks(markdown);

    return (
        <div className={styles.markdownBody}>
            {blocks.map((block, idx) => {
                switch (block.type) {
                    case 'h1':
                        return <h1 key={idx} className={styles.docH1}>{renderInline(block.text)}</h1>;
                    case 'h2':
                        return <h2 key={idx} className={styles.docH2}>{renderInline(block.text)}</h2>;
                    case 'h3':
                        return <h3 key={idx} className={styles.docH3}>{renderInline(block.text)}</h3>;
                    case 'h4':
                        return <h4 key={idx} className={styles.docH4}>{renderInline(block.text)}</h4>;
                    case 'p':
                        return <p key={idx} className={styles.docP}>{renderInline(block.text)}</p>;
                    case 'hr':
                        return <hr key={idx} className={styles.docHr} />;
                    case 'lead':
                        return (
                            <div key={idx} className={styles.leadCard}>
                                {renderInline(block.text)}
                            </div>
                        );
                    case 'wireframe':
                        return block.wireframeType === 'passport' ? (
                            <PassportWireframe key={idx} />
                        ) : (
                            <AdminScreenWireframe key={idx} />
                        );
                    case 'grid':
                        return (
                            <div key={idx} className={styles.cardGrid} style={{ gridTemplateColumns: `repeat(${block.cols}, 1fr)` }}>
                                {block.cards.map((card, cIdx) => (
                                    <div key={cIdx} className={styles.featureCard}>
                                        <div className={styles.cardHeader}>
                                            <div className={styles.cardIconBox}>
                                                {getCardIcon(card.icon)}
                                            </div>
                                            <h3 className={styles.cardTitle}>{card.title}</h3>
                                        </div>
                                        <div className={styles.cardContent}>
                                            {renderRichContent(card.content)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        );
                    case 'admonition':
                        return (
                            <div key={idx} className={`${styles.docAdmonition} ${styles['admonition_' + block.calloutType]}`}>
                                {block.title && (
                                    <div className={styles.admonitionTitle}>
                                        <span className={styles.admonitionIcon}>
                                            {block.calloutType === 'tip' ? '💡' :
                                             block.calloutType === 'keyboard' ? '⌨️' :
                                             block.calloutType === 'shield' ? '🛡️' :
                                             block.calloutType === 'warning' ? '⚠️' : 'ℹ️'}
                                        </span>
                                        <span>{block.title}</span>
                                    </div>
                                )}
                                <div className={styles.admonitionBody}>
                                    {renderRichContent(block.content)}
                                </div>
                            </div>
                        );
                    case 'callout':
                        return (
                            <div key={idx} className={`${styles.docCallout} ${styles['callout_' + block.calloutType]}`}>
                                <div className={styles.calloutIcon}>
                                    {block.calloutType === 'warning' ? '⚠️' : block.calloutType === 'tip' ? '💡' : 'ℹ️'}
                                </div>
                                <div className={styles.calloutText}>{renderInline(block.text)}</div>
                            </div>
                        );
                    case 'ul':
                        return (
                            <ul key={idx} className={styles.docUl}>
                                {block.items.map((item, itemIdx) => (
                                    <li key={itemIdx} className={styles.docLi}>{renderInline(item)}</li>
                                ))}
                            </ul>
                        );
                    case 'ol':
                        return (
                            <ol key={idx} className={styles.docOl}>
                                {block.items.map((item, itemIdx) => (
                                    <li key={itemIdx} className={styles.docLi}>{renderInline(item)}</li>
                                ))}
                            </ol>
                        );
                    case 'code':
                        return (
                            <div key={idx} className={styles.codeBlockWrapper}>
                                {block.lang && (
                                    <div className={styles.codeBlockLang}>{block.lang}</div>
                                )}
                                <pre className={styles.docPre}>
                                    <code>{block.code}</code>
                                </pre>
                            </div>
                        );
                    case 'table':
                        return (
                            <div key={idx} className={styles.tableWrapper}>
                                <table className={styles.docTable}>
                                    <thead>
                                        <tr>
                                            {block.headers.map((h, hIdx) => (
                                                <th key={hIdx}>{renderInline(h)}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {block.rows.map((row, rIdx) => (
                                            <tr key={rIdx}>
                                                {row.map((cell, cIdx) => (
                                                    <td key={cIdx}>{renderInline(cell)}</td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        );
                    case 'sandbox':
                        return renderSandbox ? renderSandbox(block.sandboxType, idx) : null;
                    default:
                        return null;
                }
            })}
        </div>
    );
};

export default MarkdownRenderer;
